import type { AuditStatusPayload } from "../../features/project-detail/types/index.ts";
import { toBooleanOrNull, toNumber } from "../lib/format-utils.ts";

export interface ValidationResult {
  valid: boolean;
  handle?: string;
  error?: string;
  isCryptoProject?: boolean | null;
  moniData?: {
    rawPayload: unknown;
    moniScore: number;
    smartsCount: number;
    smartMentionsCount: number;
    smartTier: number;
  } | null;
  existing?: {
    id: string;
    name: string | null;
    score: number | null;
    confidence: number | null;
    lastAuditedAt: string | null;
  } | null;
}

export interface Toast {
  id: string;
  type: "success" | "error" | "info";
  title: string;
  message: string;
}

export type TaskStatus = "queued" | "processing" | "completed" | "failed" | "partial";
export type CommunityTaskStatus =
  | "none"
  | "queued"
  | "processing"
  | "completed"
  | "failed"
  | "skipped";

export interface QueueInfo {
  queueState: string | null;
  queuePosition: number | null;
  waitingMs: number | null;
}

export interface ScoringCommunityProgress extends QueueInfo {
  status: CommunityTaskStatus;
  stage: string | null;
  activeOperation: string | null;
  currentEndpoint: string | null;
  currentTarget: string | null;
  apiCallCount: number;
  endpointCallCount: number;
  currentPage: number;
  currentPageLimit: number | null;
  paginationCursor: string | null;
  hasNextPage: boolean | null;
  jobId: string | null;
  tweetsFetched: number;
  mentionsFetched: number;
  repliesFetched: number;
  quotesFetched: number;
  usersEnriched: number;
}

export interface ScoringProgress {
  projectId: string;
  handle: string;
  status: TaskStatus;
  startedAt: number;
  updatedAt: number;
  lastPolledAt: number;
  jobId: string | null;
  queueState: string | null;
  queuePosition: number | null;
  waitingMs: number | null;
  usingCachedHourlyRun: boolean;
  completedProviders: string[];
  failedProviders: string[];
  totalProviders: number;
  score?: number;
  confidence?: number;
  notes?: string;
  providerScores?: Record<string, number | null>;
  failedDetails?: Record<string, string>;
  backers?: AuditStatusPayload["backers"];
  founder?: AuditStatusPayload["founder"];
  community?: ScoringCommunityProgress;
}

export interface ProjectSubmitQueue {
  status?: "queued" | "already_queued" | "skipped";
  jobId?: string;
  isNew?: boolean;
  reason?: string;
}

export interface CommunityStatusPayload {
  status: "none" | "queued" | "processing" | "completed" | "failed";
  statusSource?: "queue" | "durable_run" | "none";
  queueState: string | null;
  queuePosition: number | null;
  waitingMs: number | null;
  jobId: string | null;
  queueLookupSource?: "index" | "scan" | "none";
  staleQueueMapping?: boolean;
  progress: Record<string, unknown> | null;
  latestReport: {
    id: string;
    generatedAt: string;
    windowStart: string | null;
    windowEnd: string | null;
    windowDays: number | null;
    counts: Record<string, unknown> | null;
  } | null;
}

export interface ScoringBatchStatusItem {
  projectId: string;
  audit: AuditStatusPayload | null;
  community: CommunityStatusPayload | null;
}

export type CommunityTargetSource = "database" | "manual" | "paste" | "file";

export interface CommunityBatchTarget {
  id: string;
  handle: string;
  name: string | null;
  source: CommunityTargetSource;
  projectId: string | null;
}

export interface CommunityQueueItem {
  id: string;
  handle: string;
  name: string | null;
  source: CommunityTargetSource;
  projectId: string | null;
  referenceDate: string | null;
  status: "submitting" | "queued" | "processing" | "completed" | "failed" | "skipped";
  stage: string | null;
  activeOperation: string | null;
  currentEndpoint: string | null;
  currentTarget: string | null;
  apiCallCount: number;
  endpointCallCount: number;
  currentPage: number;
  currentPageLimit: number | null;
  paginationCursor: string | null;
  hasNextPage: boolean | null;
  jobId: string | null;
  tweetsFetched: number;
  mentionsFetched: number;
  repliesFetched: number;
  quotesFetched: number;
  usersEnriched: number;
  queueState: string | null;
  queuePosition: number | null;
  waitingMs: number | null;
  note: string | null;
  createdAt: number;
  updatedAt: number;
  lastPolledAt: number;
}

export interface CsvImportState {
  fileName: string;
  rows: string[][];
  delimiter: string;
  hasHeader: boolean;
  selectedColumn: number;
  startRow: number;
}

// --- Status helpers ---

export function parseCommunityStatus(
  payload?: CommunityStatusPayload,
  fallbackStatus: CommunityTaskStatus = "none"
): ScoringCommunityProgress {
  const progress = payload?.progress ?? null;
  const reportCounts = payload?.latestReport?.counts ?? null;

  return {
    status: (payload?.status ?? fallbackStatus) as CommunityTaskStatus,
    queueState: payload?.queueState ?? null,
    queuePosition: payload?.queuePosition ?? null,
    waitingMs: payload?.waitingMs ?? null,
    stage: typeof progress?.stage === "string" ? progress.stage : null,
    activeOperation:
      typeof progress?.activeOperation === "string" ? progress.activeOperation : null,
    currentEndpoint:
      typeof progress?.currentEndpoint === "string" ? progress.currentEndpoint : null,
    currentTarget: typeof progress?.currentTarget === "string" ? progress.currentTarget : null,
    apiCallCount: toNumber(progress?.apiCallCount),
    endpointCallCount: toNumber(progress?.endpointCallCount),
    currentPage: toNumber(progress?.currentPage),
    currentPageLimit: toNumber(progress?.currentPageLimit) || null,
    paginationCursor:
      typeof progress?.paginationCursor === "string" ? progress.paginationCursor : null,
    hasNextPage: toBooleanOrNull(progress?.hasNextPage),
    jobId: payload?.jobId ?? null,
    tweetsFetched: toNumber(progress?.tweetsFetched) || toNumber(reportCounts?.project_posts) || 0,
    mentionsFetched: toNumber(progress?.mentionsFetched) || toNumber(reportCounts?.mention_tweets),
    repliesFetched: toNumber(progress?.repliesFetched) || toNumber(reportCounts?.reply_tweets),
    quotesFetched: toNumber(progress?.quotesFetched) || toNumber(reportCounts?.quote_tweets),
    usersEnriched: toNumber(progress?.usersEnriched) || toNumber(reportCounts?.users_enriched),
  };
}

// --- Polling constants ---

export const ACTIVE_POLL_INTERVAL_MS = 2000;
export const WAITING_POLL_INTERVAL_MS = 3000;
export const BUSY_QUEUE_THRESHOLD_MS = 30000;

// --- Community stage formatters ---

export const COMMUNITY_STAGE_LABELS: Record<string, string> = {
  initializing: "Initializing",
  fetching_tweets: "Fetching Tweets",
  fetching_mentions: "Fetching Mentions",
  fetching_replies: "Deep Scraping Replies",
  fetching_quotes: "Deep Scraping Quotes",
  enriching_users: "Enriching Users",
  building_report: "Building Report",
  completed: "Completed",
};

export function formatCommunityStage(stage: string | null, status: CommunityTaskStatus): string {
  if (stage && COMMUNITY_STAGE_LABELS[stage]) {
    return COMMUNITY_STAGE_LABELS[stage];
  }
  if (stage) {
    return stage.replaceAll("_", " ");
  }
  if (status === "queued") return "Queued";
  if (status === "processing") return "Processing";
  if (status === "completed") return "Completed";
  if (status === "failed") return "Failed";
  if (status === "skipped") return "Skipped";
  return "Waiting";
}

export function formatPageLabel(currentPage: number, pageLimit: number | null): string {
  if (currentPage <= 0) return "n/a";
  if (pageLimit && pageLimit > 0) return `${currentPage}/${pageLimit}`;
  return String(currentPage);
}

export function formatCursorPreview(cursor: string | null): string {
  if (!cursor) return "none";
  if (cursor.length <= 22) return cursor;
  return `${cursor.slice(0, 10)}...${cursor.slice(-8)}`;
}

// --- Task status helpers ---

export function isAuditTerminalStatus(status: TaskStatus): boolean {
  return status === "completed" || status === "failed" || status === "partial";
}

export function isCommunityTerminalStatus(status: CommunityTaskStatus): boolean {
  return status === "none" || status === "completed" || status === "failed" || status === "skipped";
}

export function isScoringTaskActive(task: ScoringProgress): boolean {
  const auditDone = isAuditTerminalStatus(task.status);
  const communityDone = isCommunityTerminalStatus(task.community?.status ?? "none");
  return !(auditDone && communityDone);
}

export function getScoringTaskPollInterval(task: ScoringProgress): number {
  const communityStatus = task.community?.status ?? "none";
  const hasProcessing = task.status === "processing" || communityStatus === "processing";
  if (hasProcessing) {
    return ACTIVE_POLL_INTERVAL_MS;
  }

  const hasQueued = task.status === "queued" || communityStatus === "queued";
  return hasQueued ? WAITING_POLL_INTERVAL_MS : ACTIVE_POLL_INTERVAL_MS;
}

export function getCommunityStatusLabel(status: CommunityQueueItem["status"]): string {
  if (status === "submitting") return "Submitting";
  if (status === "queued") return "Queued";
  if (status === "processing") return "Processing";
  if (status === "completed") return "Completed";
  if (status === "failed") return "Failed";
  return "Skipped";
}

export function toCommunityTaskStatus(status: CommunityQueueItem["status"]): CommunityTaskStatus {
  if (status === "submitting") return "queued";
  return status;
}
